import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  ButtonDirective,
  CardBodyComponent,
  CardComponent,
  CardGroupComponent,
  ColComponent,
  ContainerComponent,
  FormControlDirective,
  FormDirective,
  FormFeedbackComponent,
  FormLabelDirective,
  InputGroupComponent,
  InputGroupTextDirective,
  RowComponent,
  SpinnerComponent,
  TextColorDirective
} from '@coreui/angular-pro';
import { IconDirective, IconModule, IconSetService } from '@coreui/icons-angular';
import { cilEye, cilEyeSlash, cilLockLocked } from '@coreui/icons-pro';
import { TranslatePipe } from 'i18n';
import { Logger, LogoComponent } from 'shared';

@Component({
  selector: 'app-confirm-email-setup-page',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    ContainerComponent,
    RowComponent,
    ColComponent,
    CardGroupComponent,
    TextColorDirective,
    CardComponent,
    CardBodyComponent,
    ButtonDirective,
    IconDirective,
    IconModule,
    SpinnerComponent,
    FormDirective,
    FormControlDirective,
    FormLabelDirective,
    FormFeedbackComponent,
    InputGroupComponent,
    InputGroupTextDirective,
    LogoComponent,
    TranslatePipe
  ],
  templateUrl: './confirm-email-setup-page.component.html',
  styleUrls: ['./confirm-email-setup-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ConfirmEmailSetupPageComponent {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private http = inject(HttpClient);
  private fb = inject(FormBuilder);
  private iconSet = inject(IconSetService);
  private destroyRef = inject(DestroyRef);
  protected logger = new Logger('ConfirmEmailSetupPageComponent');

  // ────────────────────────────────────────────────
  // Reactive state (signals)
  // ────────────────────────────────────────────────
  loading = signal<boolean>(true);
  validating = signal<boolean>(true);
  tokenValid = signal<boolean>(false);
  success = signal<boolean>(false);
  error = signal<string | null>(null);
  email = signal<string>('');
  firstName = signal<string>('');
  passwordVisible = signal<boolean>(false);
  confirmPasswordVisible = signal<boolean>(false);

  // Form
  passwordForm: FormGroup = this.fb.group(
    {
      password: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', Validators.required]
    },
    { validators: this.passwordMatchValidator }
  );

  // Computed
  password = this.passwordForm.get('password')!;
  confirmPassword = this.passwordForm.get('confirmPassword')!;

  private formStatus = toSignal(this.passwordForm.statusChanges, { initialValue: this.passwordForm.status });

  canSubmit = computed(() =>
    this.tokenValid() &&
    !this.loading() &&
    this.formStatus() === 'VALID'
  );

  // ────────────────────────────────────────────────
  // Effects
  // ────────────────────────────────────────────────
  constructor() {
    this.iconSet.icons = { ...this.iconSet.icons, cilEye, cilEyeSlash, cilLockLocked };

    // React to route token once
    effect(() => {
      const token = this.route.snapshot.params['token'];
      this.logger.debug('Confirmation token:', token);

      if (!token) {
        this.validating.set(false);
        this.loading.set(false);
        this.tokenValid.set(false);
        this.error.set('Invalid confirmation link');
        return;
      }

      this.validateToken(token);
    });
  }

  // ────────────────────────────────────────────────
  // Core logic
  // ────────────────────────────────────────────────
  private validateToken(token: string): void {
    this.loading.set(true);
    this.error.set(null);

    this.http.get<{ valid: boolean; message?: string; email?: string; first_name?: string }>(
      `/auth/verify_confirmation_token/${token}`
    )
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe({
      next: response => {
        this.validating.set(false);
        this.tokenValid.set(response.valid);

        if (response.valid) {
          this.email.set(response.email || '');
          this.firstName.set(response.first_name || '');
          this.loading.set(false);
        } else {
          this.error.set(response.message || 'Invalid or expired token');
          this.loading.set(false);
        }
      },
      error: err => {
        this.logger.error('Token validation failed', err);
        this.validating.set(false);
        this.tokenValid.set(false);
        this.loading.set(false);
        this.error.set(err.error?.message || err.error?.error || 'Token validation failed');
      }
    });
  }

  private passwordMatchValidator(formGroup: FormGroup) {
    const password = formGroup.get('password');
    const confirmPassword = formGroup.get('confirmPassword');

    if (!password || !confirmPassword) return null;

    if (confirmPassword.errors && !confirmPassword.errors['passwordMismatch']) {
      return null;
    }

    if (password.value !== confirmPassword.value) {
      confirmPassword.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    } else {
      confirmPassword.setErrors(null);
      return null;
    }
  }

  onSubmit(): void {
    if (this.passwordForm.invalid || !this.tokenValid()) return;

    this.loading.set(true);
    this.error.set(null);

    const { password } = this.passwordForm.value;

    this.http.post<{ success: boolean; message?: string; error?: string }>(
      '/auth/confirm_email_and_setup_password',
      { token: this.route.snapshot.params['token'], password }
    )
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe({
      next: response => {
        this.logger.debug('Email confirmed and password set', response);
        this.loading.set(false);
        this.success.set(true);
        this.error.set(null);

        // Auto-redirect after 3 seconds
        setTimeout(() => {
          this.router.navigate(['/login']);
        }, 3000);
      },
      error: err => {
        this.logger.error('Confirmation failed', err);
        this.loading.set(false);
        this.success.set(false);
        this.error.set(err.error?.error || 'Failed to confirm email and set password');
      }
    });
  }

  togglePasswordVisibility(): void {
    this.passwordVisible.update(v => !v);
  }

  toggleConfirmPasswordVisibility(): void {
    this.confirmPasswordVisible.update(v => !v);
  }
}